<template>
  <div class="settings-view">
    <PageHeader title="Maintenance" subtitle="Database housekeeping. These operations affect the whole workspace." />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!info" :error="loadError" @retry="refresh">
      <div class="settings-form-card">
        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Database</div>
            <div class="setting-row-desc">Current on-disk size and amount of stored document history.</div>
          </div>
          <div class="setting-control">
            <div class="maintenance-stats">
              <div class="maintenance-stat">
                <span class="maintenance-stat-label">Database size</span>
                <span class="maintenance-stat-value">{{ formatBytes(databaseSizeBytes) }}</span>
              </div>
              <div class="maintenance-stat">
                <span class="maintenance-stat-label">Document history entries</span>
                <span class="maintenance-stat-value">{{ historyCount.toLocaleString() }}</span>
              </div>
            </div>
            <button type="button" class="btn" :disabled="busy" @click="refresh">Refresh</button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Clear all documents history</div>
            <div class="setting-row-desc">
              Permanently deletes every document history entry and diff. The documents themselves are kept.
              This cannot be undone.
            </div>
          </div>
          <div class="setting-control">
            <button type="button" class="btn btn-danger" :disabled="busy" @click="confirmOpen = true">
              {{ busyClear ? 'Clearing…' : 'Clear history' }}
            </button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Compact database (VACUUM)</div>
            <div class="setting-row-desc">
              Rebuilds the database file to reclaim unused space. Safe to run, but may take a moment on large databases.
            </div>
          </div>
          <div class="setting-control">
            <button type="button" class="btn" :disabled="busy" @click="runVacuum">
              {{ busyVacuum ? 'Running…' : 'Run VACUUM' }}
            </button>
            <div v-if="vacuumResult" class="maintenance-result">
              <span class="maintenance-result-part">Before <strong>{{ formatBytes(vacuumResult.before) }}</strong></span>
              <ArrowRight class="icon maintenance-result-arrow" />
              <span class="maintenance-result-part">After <strong>{{ formatBytes(vacuumResult.after) }}</strong></span>
              <span class="maintenance-result-freed">{{ formatFreed(vacuumResult.before, vacuumResult.after) }}</span>
            </div>
          </div>
        </div>
      </div>
    </AsyncState>

    <ConfirmDialog
      v-if="confirmOpen"
      title="Clear all documents history"
      :message="confirmMessage"
      confirm-label="Clear history"
      danger
      @confirm="clearHistory"
      @cancel="confirmOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import {
  CommandsService,
  QueriesService,
  type GetMaintenanceInfoQueryResponse
} from '../api/generated';
import ConfirmDialog from '../components/ConfirmDialog.vue';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

const info = ref<GetMaintenanceInfoQueryResponse | null>(null);
const session = useSessionStore();
const message = ref<string | null>(null);
const loadError = ref<string | null>(null);
const busyClear = ref(false);
const busyVacuum = ref(false);
const busyRefresh = ref(false);
const confirmOpen = ref(false);
const vacuumResult = ref<{ before: number; after: number } | null>(null);

const busy = computed(() => busyClear.value || busyVacuum.value || busyRefresh.value);
const databaseSizeBytes = computed(() => Number(info.value?.databaseSizeBytes ?? 0));
const historyCount = computed(() => Number(info.value?.documentHistoryEntryCount ?? 0));
const confirmMessage = computed(
  () => `Delete all ${historyCount.value.toLocaleString()} document history entries? This cannot be undone.`
);

onMounted(() => load(true));

async function load(useBootstrap = false): Promise<void> {
  busyRefresh.value = true;
  loadError.value = null;
  try {
    info.value = useBootstrap
      ? session.pageBootstrap?.maintenance ?? null
      : await QueriesService.queryGetMaintenanceInfoQuery({ requestBody: {} });
    if (info.value == null) {
      throw new Error('Maintenance bootstrap payload was missing.');
    }
  } catch (error) {
    const text = getErrorMessage(error, 'Unable to load maintenance information.');
    if (info.value) {
      message.value = text;
    } else {
      loadError.value = text;
    }
  } finally {
    busyRefresh.value = false;
  }
}

async function refresh(): Promise<void> {
  await load();
}

async function clearHistory(): Promise<void> {
  confirmOpen.value = false;
  busyClear.value = true;
  message.value = null;
  try {
    const result = await CommandsService.commandClearDocumentHistoryCommand({ requestBody: {} });
    message.value = `Deleted ${Number(result.deletedEntryCount).toLocaleString()} history entries.`;
    if (info.value != null) {
      info.value.documentHistoryEntryCount = 0;
    }
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to clear documents history.');
  } finally {
    busyClear.value = false;
  }
}

async function runVacuum(): Promise<void> {
  busyVacuum.value = true;
  message.value = null;
  try {
    const result = await CommandsService.commandVacuumDatabaseCommand({ requestBody: {} });
    vacuumResult.value = {
      before: Number(result.sizeBeforeBytes),
      after: Number(result.sizeAfterBytes)
    };
    if (info.value != null) {
      info.value.databaseSizeBytes = Number(result.sizeAfterBytes);
    }
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to compact the database.');
  } finally {
    busyVacuum.value = false;
  }
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatFreed(before: number, after: number): string {
  const freed = before - after;
  if (freed <= 0) {
    return 'No space reclaimed';
  }
  return `${formatBytes(freed)} freed`;
}
</script>

<style scoped>
.maintenance-stats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
}

.maintenance-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.maintenance-stat-label {
  color: var(--text-muted);
  font-size: 0.78rem;
}

.maintenance-stat-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.maintenance-result {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  color: var(--text-muted);
  font-size: 0.82rem;
}

.maintenance-result strong {
  color: var(--text-main);
  font-weight: 500;
}

.maintenance-result-arrow {
  width: 14px;
  height: 14px;
}

.maintenance-result-freed {
  color: var(--accent);
  font-weight: 500;
}
</style>
